/**
 * ConversationLayoutManager.js
 * Talkroute
 *
 * Created by Vel Genov on 2/3/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import InfiniteScroll from "react-infinite-scroll-component";
import { CSSTransition } from "react-transition-group";
import {
  fetchNextBatchConversations,
  fetchNextBatchFilteredConversations,
  fetchNewConversations,
  updateReadStatus,
  deleteConversation,
  MULTIPLE_SELECTION_TYPES,
  switchConversationSelectionMode,
  selectMultipleConversation,
} from "../actions/conversationActions";
import MessageInputBar from "../components/conversationComponents/MessageInputBar";
import {
  ConversationData,
  NEW_CONVERSATION_ID,
  WELCOME_CONVERSATION,
  conversationJSONParser,
} from "../services/dataClasses/ConversationData";
import uiString from "../constants/uiString";
import MessageFileDropZone from "../components/conversationComponents/MessageFileDropZone";
import ProgressIndicator from "../components/conversationComponents/ProgressIndicator";
import MessagesContextMenu from "@components/MessagesContextMenu";
import { SelectedConversations } from "@components/conversationComponents/SelectedConversations";
import { createStyles, Grid, makeStyles, Theme } from "@material-ui/core";
import clsx from "clsx";
import { useWindowSize } from "@helpers/hooks/useWindowsSize.hook";
import { useAppSelector } from "@helpers/hooks/useAppSelector.hook";
import useLocalStorage, { localStorageItems } from "@helpers/hooks/useLocalStorage.hook";
import { IUserMetadata } from "@models/LocalStorageItems.models";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      minHeight: "calc(100% - 60px)",
      height: "calc(100% - 60px)",
      margin: "0px !important",
      overflow: "hidden",
      background: "var(--color-light-grey)",
      flexWrap: "nowrap",
    },
    conversationsItemStyle: {
      height: "100%",
      overflowY: "auto",
      overflowX: "hidden",
    },
    conversationListViewStyle: {
      padding: "0px !important",
      backgroundColor: "var(--color-background-grey)",
    },
    conversationInputBar: {
      padding: "0px",
      display: "flex",
      flexFlow: "column",
      height: "100%",
      borderLeft: "1px solid var(--color-dark-grey)",
      flex: "1",
    },
    messageFileDropZone: {
      flexGrow: 0,
      flexShrink: 1,
      flexBasis: "auto",
    },
  })
);

interface IConversationLayoutManagerProps {
  showEmptyConversations: any;
  conversationsList: any;
  children: any;
}

/**
 * A container React component. Implemented following the composition paradigm
 * https://reactjs.org/docs/composition-vs-inheritance.html
 *
 * Displays the layout of a conversation:
 * * Empty conversation list message
 * * Conversation List
 * * The currently selected conversation
 *
 * @param showEmptyConversations Shown only if there are no conversations to display
 * @param conversationsList The list of conversation displayed on the right
 * @param currentConversation The current conversation displayed on the left
 */
const ConversationLayoutManager = ({
  showEmptyConversations,
  conversationsList,
  children,
}: IConversationLayoutManagerProps) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const _conversations = useAppSelector(state => state.conversations.data);
  const temporaryConversation = useAppSelector(state => state.conversations.temporaryConversation);
  const parsedConversation = useMemo(() => {
    if (temporaryConversation) {
      return temporaryConversation;
    } else {
      return null;
    }}, [temporaryConversation])
  const conversations = useMemo(() => {
    if (parsedConversation) {
      const isTemporaryConversationInConversations = _conversations.some(conversation => conversation.conversationId === parsedConversation.conversationId);
      if (!isTemporaryConversationInConversations) {
        return [parsedConversation, ..._conversations];
      }
    }
    return _conversations;
  }, [_conversations, parsedConversation])
  const conversationPagination = useAppSelector(
    state => state.conversations.conversationPagination
  );
  const selectedConversationId = useAppSelector(
    state => state.conversations.selectedConversationId
  );
  const conversationDraft = useAppSelector(
    state => state.conversations.conversationDraft
  );
  const filterBy = useAppSelector(state => state.conversations.filterBy);
  const multipleSelectionMode = useAppSelector(
    state => state.conversations.multipleSelectionMode
  );
  const [openFileTrigger, setOpenFileTrigger] = useState(null);
  const [isLoadingPicture, setIsLoadingPicture] = useState(false);
  const [xPosMenu, setXPosMenu] = useState("0px");
  const [yPosMenu, setYPosMenu] = useState("0px");
  const [showMenu, setShowMenu] = useState(false);
  const conversationScroll = useRef<HTMLDivElement>(null);
  const { height } = useWindowSize();
  const conversationsScrolled = () => dispatch(fetchNextBatchConversations());
  // this is only talkroute numbers for now
  const filteredConversationsScrolled = () => dispatch(fetchNextBatchFilteredConversations())
  const refreshConversations = () => dispatch(fetchNewConversations());
  const userId = useAppSelector(state => state.authentication.userId);
  const [metadata, setMetadata] = useLocalStorage<IUserMetadata[]>(localStorageItems.metadata, []);
  const { totalConversationsOnServer } = useAppSelector(state => state.conversations);
  
  useEffect(() => {
    if (
      selectedConversationId !== NEW_CONVERSATION_ID &&
      selectedConversationId !== null
    ) {
      const currentConversation = conversations.find(
        (conversation) => conversation.conversationId === selectedConversationId
      );

      if (currentConversation) {
        const { isRead } = currentConversation.lastMessage;
        if (!isRead) markAsRead(currentConversation.conversationId);
      }
    }
  }, [selectedConversationId]);

  /**
   * Request to mark the current conversation as read
   */
  const markAsRead = (conversationId: string) => {
    if (conversationId === WELCOME_CONVERSATION) {
      const existingUserDataIndex = metadata.findIndex(userData => userData.userId === userId);
      if (existingUserDataIndex !== -1) {
        const newMetadata = metadata.map((userData, index) => 
          index === existingUserDataIndex ? ({...userData, isWelcomeConversationRead: true}) : userData
        )
        setMetadata(newMetadata)
      } else {
        setMetadata([...metadata, {
          userId: userId as number,
          isWelcomeVoicemailDeleted: false,
          isWelcomeVoicemailRead: false,
          isWelcomeConversationDeleted: false,
          isWelcomeConversationRead: true,
        }])
      }
    }
    if (conversationId !== NEW_CONVERSATION_ID) {
      dispatch(
        updateReadStatus({
          conversationId,
          readStatus: true,
        })
      );
    }
  };

  /**
   * show context menu for convesations
   */
  const showConversationsMenu = (event: any) => {
    event.preventDefault();
    const HORIZONTAL_POS_LIMIT = 600;
    const VERTICAL_POS_LIMIT = height - (multipleSelectionMode ? 200 : 150);
    const xPosMenu = Math.min(event.pageX - 2, HORIZONTAL_POS_LIMIT) + "px";
    const yPosMenu = Math.min(event.pageY - 15, VERTICAL_POS_LIMIT) + "px";
    setShowMenu(true);
    setXPosMenu(xPosMenu);
    setYPosMenu(yPosMenu);
  };

  const hideConversationMenu = () => setShowMenu(false);

  /**
   * Called when all messages are selected
   *
   * @param conversationId is empty
   */
  const onSelectedAllEntries = (conversationId = "") => {
    dispatch(switchConversationSelectionMode(true));
    dispatch(selectMultipleConversation(conversationId, MULTIPLE_SELECTION_TYPES.ALL));
    hideConversationMenu();
  };

  const conversationListLength = conversationsList
    ? conversationsList.length
    : 0;

  const totalConversationsLength = conversations ? conversations.length : 0;
  const hasMoreConversations = totalConversationsOnServer > totalConversationsLength;

  return (
    <CSSTransition in appear key={filterBy} timeout={300} classNames="fade">
      <Grid container className={classes.root}>
        <Grid
          item
          className={clsx(classes.conversationListViewStyle, "middle-cell")}
        >
          {/* Show the list of conversations */}
          <div
            id="conversationsScroll"
            ref={conversationScroll}
            className={classes.conversationsItemStyle}
            onContextMenu={(e) => showConversationsMenu(e)}
            >
            <InfiniteScroll
              loader={<h4>Loading...</h4>}
              style={{
                overflowX: "hidden",
              }}
              dataLength={conversationListLength}
              next={filterBy == "All Messages" ? conversationsScrolled : filteredConversationsScrolled}
              hasMore={hasMoreConversations}
              scrollableTarget="conversationsScroll"
              endMessage={null}
              refreshFunction={refreshConversations}
              pullDownToRefresh
              pullDownToRefreshContent={
                <h3 style={{ textAlign: "center" }}>
                  &#8595; {`${uiString.PULL_TO_REFRESH}`}
                </h3>
              }
              releaseToRefreshContent={
                <h3 style={{ textAlign: "center" }}>
                  &#8593; {`${uiString.RELEASE_TO_REFRESH}`}
                </h3>
              }
            >
              {conversationsList}
              {showEmptyConversations}
            </InfiniteScroll>
          </div>

            <MessagesContextMenu
              position={{ xPosMenu, yPosMenu }}
              showMenu={showMenu}
              selectAllEntries={onSelectedAllEntries}
              hideConversationMenu={hideConversationMenu}
              conversationScrollRef={conversationScroll}
            />
        </Grid>
        {/* Show the current conversation, if available */}
        {multipleSelectionMode ? (
          <SelectedConversations />
        ) : children ? (
          <Grid item className={classes.conversationInputBar}>
            <ProgressIndicator />
            {children}
            <MessageFileDropZone
              className={classes.messageFileDropZone}
              openFileTriggerCallback={setOpenFileTrigger}
              pictureLoadingCallback={setIsLoadingPicture}
            >
              <MessageInputBar
                conversationId={String(selectedConversationId)}
                conversationDraft={conversationDraft}
                openFileTrigger={openFileTrigger}
                isLoading={isLoadingPicture}
              />
            </MessageFileDropZone>
          </Grid>
        ) : (
          <Grid
            item
            // width={conversationLayoutWidth}
            style={{ borderLeft: "1px solid var(--color-dark-grey)" }}
          >
            <h5 style={{ textAlign: "center" }}>
              {uiString.NO_DETAILS_AVAILABLE}
            </h5>
          </Grid>
        )}
      </Grid>
    </CSSTransition>
  );
};

export default ConversationLayoutManager;
